import {SegmentedControl} from '@mantine/core';
import {useRouter} from 'next/router';
import type React from 'react';
import {useMemo} from 'react';
import type {ReactNode} from 'react';
import {
  COLOR_BACKGROUND_1,
  COLOR_BLACK_1,
  COLOR_BLACK_2,
} from '../../../../../../config/constants/colorCodes';
import {
  ENTRY_MANAGEMENTS_CALENDAR,
  ENTRY_MANAGEMENTS_PROGRESS,
  ENTRY_MANAGEMENTS_TODO,
} from '../../../../../../config/constants/routes';
import {useIsAppWebView} from '../../../../../../shared/hooks/useIsAppWebView';
import {useEntryManagementReminders} from '../../../../usecase/useEntryManagementReminders';

/**
 * EntryManagementTabsコンポーネントのProps
 */
type Props = {
  /** 現在選択されているタブの値 */
  value: string;
};

/**
 * リマインダーバッジ付きラベルのProps
 */
type LabelWithBadgeProps = {
  /** 表示するラベルテキスト */
  label: string;
  /** リマインダーバッジを表示するかどうか */
  hasReminder: boolean;
};

/**
 * タブデータの項目
 */
type TabDataItem = {
  /** タブの識別値（ルートパス） */
  value: string;
  /** タブに表示するラベル（文字列またはReactノード） */
  label: string | ReactNode;
};

/**
 * リマインダーバッジのサイズ定数
 */
const REMINDER_BADGE_SIZE = 8;

/**
 * リマインダーバッジ付きラベルコンポーネント
 * 未確認の通知がある場合、ラベルの横に赤いドットを表示
 * インジケータを含めた領域全体が中央揃えになる
 */
const LabelWithBadge: React.FC<LabelWithBadgeProps> = ({
  label,
  hasReminder,
}) => (
  <div className="flex items-center justify-center gap-4">
    <span>{label}</span>
    {hasReminder && (
      <span
        className="bg-notification rounded-full"
        style={{
          width: `${REMINDER_BADGE_SIZE}px`,
          height: `${REMINDER_BADGE_SIZE}px`,
        }}
        aria-label={`${label}に未確認の通知があります`}
        role="status"
      />
    )}
  </div>
);

/**
 * タブデータを生成する関数
 * リマインダーの状態に応じてバッジ付きのラベルを生成する
 *
 * @param hasProgressReminder - 選考管理タブにリマインダーがあるか
 * @param hasTodoReminder - ToDoタブにリマインダーがあるか
 * @returns タブデータの配列
 */
const createTabDataList = (
  hasProgressReminder: boolean,
  hasTodoReminder: boolean,
): TabDataItem[] => [
  {
    value: ENTRY_MANAGEMENTS_PROGRESS,
    label: (
      <LabelWithBadge label="選考管理" hasReminder={hasProgressReminder} />
    ),
  },
  {
    value: ENTRY_MANAGEMENTS_TODO,
    label: <LabelWithBadge label="ToDo" hasReminder={hasTodoReminder} />,
  },
  {
    value: ENTRY_MANAGEMENTS_CALENDAR,
    label: 'カレンダー',
  },
];

/**
 * エントリー管理のタブナビゲーション
 *
 * リマインダーがある場合は各タブに赤いドットバッジを表示する。
 * SP表示時に使用し、SegmentedControlでタブ切り替えを提供する。
 *
 * @remarks
 * WebView環境ではlocation.hrefで遷移し、通常環境ではNext.jsのRouterを使用する。
 * これはWebView環境でrouterを使うとネイティブとの連携が正しく動作しないため。
 */
export const EntryManagementTabs: React.FC<Props> = ({value}) => {
  const router = useRouter();
  const {hasTodoReminder, hasProgressReminder} = useEntryManagementReminders();
  const isWebView = useIsAppWebView();

  // タブデータをメモ化してリマインダー状態変更時のみ再生成
  const tabDataList = useMemo(
    () => createTabDataList(hasProgressReminder, hasTodoReminder),
    [hasProgressReminder, hasTodoReminder],
  );

  /**
   * タブ変更時のハンドラー
   * 現在のパスと異なる場合のみ遷移を実行
   */
  const handleTabChange = (newValue: string) => {
    if (!newValue || router.asPath === newValue) {
      return;
    }

    if (isWebView) {
      location.href = newValue;
    } else {
      void router.push(newValue);
    }
  };

  return (
    <SegmentedControl
      key="entry-management-tabs"
      value={value}
      onChange={handleTabChange}
      data={tabDataList}
      fullWidth
      styles={{
        root: {
          backgroundColor: COLOR_BACKGROUND_1,
          borderRadius: '4px',
          padding: '4px',
        },
        label: {
          fontWeight: 700,
          color: COLOR_BLACK_2,
          padding: '8px 12px',
          '&[data-active]': {
            color: COLOR_BLACK_1,
          },
        },
        control: {
          border: 'none',
          '&:not(:first-of-type)': {
            borderLeft: 'none',
          },
          '&::before': {
            display: 'none',
          },
        },
      }}
      transitionDuration={0}
      aria-label="エントリー管理タブ"
    />
  );
};
